import { normalizeControlLookupVariant, type ControlLookupVariant } from '../aeppic/forms/preload-controls.js'
import * as Types from '@aeppic/types'
import { parse as parseFormDown, markdown } from '@aeppic/forms-parser'
import type { ControlParameters as ParsedControlParameters, ParsedField, ParsedTag, ParsedTags, ParsedFormInfo, ParsedFormSection, ParsedPlaceholder, ParsedTagWithExpression } from '@aeppic/forms-parser'
import { camelCase } from '@aeppic/shared/camel-case'
import { DateTime, Duration as DateTimeDuration } from 'luxon'
import { lookupDefaultControlName } from '../aeppic/utils/control-names.js'

// import Document from './document'
import Reference from './reference.js'

import { buildLogger } from './log.js'

const log = buildLogger('form')

export type { ParsedPlaceholder }

import createControllerClass from '../aeppic/create-controller-class.js'
import { FieldFormatter, createFormattingStepCodes } from './field-formatter.js'
import { EditableDocument } from './editable-document.js'

function fieldTagHasExpression(tag: ParsedTag): tag is ParsedTagWithExpression {
  return (tag && typeof tag === 'object' && !!tag.expression)
}

export type TagMatch = string | RegExp | { name: string | RegExp, value: string | RegExp }
export type TagMatchOptions = { operation?: 'and' | 'or' }
export type TagMatchInfo = {
  name?: string
  value?: string
  matchName?: RegExp
  matchValue?: RegExp
}
export type FileField = {
  name: string
  size: number
  type: string
  sha1: string
  dataUrl: string,
  thumbnailUrl: string,
  iconUrl: string,
  previewUrl: string,
  fileInfo: {
    created: number,
    read: number,
    modified: number,
    imported: number
  }
  mediaInfo?: {
    width: number,
    height: number
  }
}

export class FormVersions {
 
  // all versions latest version is index 0
  private _deleted = false
  private _versions = new Map<string, Form>()

  constructor(private _id: string, private _formOptions: FormOptions = {}) {}

  get id() {
    return this._id
  }

  delete() {
    this._deleted = true
  }

  get deleted() {
    return this._deleted
  }

  has(version: string): boolean {
    return  this._versions.has(version)
  }

  getVersions(): string[] {
    const forms: Form[] = Array.from(this._versions.values())
    forms.sort((a, b) => a.compare(b))
    return forms.map(f => f.v)
  }

  getLatestVersion(): string {
    const versions = this.getVersions()
    return versions[versions.length - 1]
  }

  add(formDocument: Types.Document) {
    if (formDocument.id !== this._id) {
      throw new Error(`Invalid form document argument. Wrong id`)
    }

    if (formDocument.f.id !== 'form') {
      throw new Error('Invalid form document argument. Requires document of type `form`')
    }

    const form = new Form(formDocument, this._formOptions)
    this._versions.set(form.v, form)
  }

  get(version: string): Form {
    return this._versions.get(version)
  }

  getAll(): Form[] {
    return Array.from(this._versions.values())
  }
}

function isInitialFormForm(form: Form) {
  return (form.id === 'form' && form.v === 'initial')
}

function hasTextIcon(value: unknown) {
  return typeof value === 'string' && value.trim().length > 0
}

function hasFileIcon(value: unknown) {
  if (!value || typeof value !== 'object') {
    return false
  }

  const file = value as Partial<FileField>

  return hasTextIcon(file.dataUrl)
    || hasTextIcon(file.thumbnailUrl)
    || hasTextIcon(file.iconUrl)
    || hasTextIcon(file.previewUrl)
    || hasTextIcon(file.sha1)
    || (typeof file.size === 'number' && file.size > 0)
}

export interface FormOptions {
  version?: string
  temporary?: boolean
  typeValidation?: TypeValidationOptions
  parseViews?: boolean
}

export interface TypeValidationOptions {
  strictTemporalTypes?: boolean
  dateTimeToDateFieldMode?: 'preserve' | 'strip'
  /**
   * The instance's IANA zone (e.g. `Europe/Berlin`). DateTime values assigned
   * to datetime fields, and to non-strict date fields, are converted to it and
   * stored with the zone (`2026-10-13T19:00:00.000+02:00[Europe/Berlin]`).
   * Without one they keep their own offset and carry no zone.
   */
  timezone?: string | null
}

export type FieldInfo = ParsedField

export type ParsedFormViewInfo = {
  name: string
  definition: string
  info: ParsedFormInfo
}

export type ParsedFormViews = {
  [name: string]: ParsedFormViewInfo
}

type ParsedFormInfoWithViews = ParsedFormInfo & {
  views?: ParsedFormViews
}

export type FieldValidationDeclaration = {
  field: FieldInfo
  fieldName: string
  validations: string[]
}

export type PlaceholderCondition = {
  id: string
  expression: string
}

export type FormFieldChangeEntryOptions = {
  includeHidden?: boolean
}

export type FormFieldChangeEntry = {
  name: string
  label: string
  sectionPath: string[]
  sectionIndex: number
  placeholderOffset: number
  placeholder: ParsedPlaceholder
}

export interface IFormSection {
  get index(): number
  get title(): string
  get type(): 'ROOT'|'ATX'|'SETEXT'|'VIRTUAL'
  get tags(): ParsedTags
  get level(): number
  get style(): ParsedStyleParameters
  get paragraphs(): IFormSectionParagraph[]
  get subSections(): IFormSection[]
  get offset(): number
}

export class FormSection implements IFormSection {
  private _section: ParsedFormSection
  private _paragraphs: IFormSectionParagraph[] = null
  private _subSections: IFormSection[] = null

  constructor(section: ParsedFormSection, formInfo: ParsedFormInfo) {
    this._section = section
    this._paragraphs = section.paragraphs.map((p: any, index: number) => new FormSectionParagraph(p, formInfo))
    this._subSections = (section.directSubsections ?? []).map((s: any, index: number) => new FormSection(s, formInfo))
  }

  public get offset() {
    return this._section.offset
  }

  public get index() {
    return this._section.index
  }

  public get title() {
    return this._section.title
  }

  public get type() {
    return this._section.type
  }

  public get level() {
    return this._section.level
  }

  public get style() {
    return this._section.style
  }

  public get tags() {
    return this._section.tags
  }

  public get paragraphs() {
    return this._paragraphs
  }

  public get subSections() {
    return this._subSections
  }
}

export type DataValidationErrors = {
  [fieldName: string]: FieldValidationErrors
}

export type FieldValidationError = { text: string, html: string }

export type FieldValidationErrors = {
  required?: FieldValidationError
  cardinality?: FieldValidationError
  type?: FieldValidationError
  format?: FieldValidationError
  bounds?: FieldValidationError
}

// TODO: Implement inline with text mode
// export class ParagraphItemType {
//   static TEXT = 'TEXT'
//   static FIELD = 'FIELD'
//   static STANDALONE_CONTROL = 'STANDALONE_CONTROL'
// }

// export class ParagraphType {
//   // static TEXT = ParagraphItemType.TEXT
//   // static FIELD = ParagraphItemType.FIELD
//   // static STANDALONE_CONTROL = ParagraphItemType.STANDALONE_CONTROL
//   // static INLINE = 'INLINE'
//   // static INLINE_WITH_TEXT = 'INLINE_WITH_TEXT'
// }

export type FormControlParameterValue = string | number | boolean | {
  [key: string]: FormControlParameterValue
}

export type ControlParameters = {
  [name: string]: FormControlParameterValue
}

/*
 * 
 * @example
 * const test: FormControlInfo = { 
 *   "namespace": "",
 *   "name": "ref",
 *   "fullName": "ref",
 *   "parameters": {
 *     "hide-path": {
 *       "value": "true"
 *      },
 *      "ancestor-search-limit": {
 *        "value": "2"
 *      },
 *      "handle-selected": {
 *        "value": "false"
 *      }
 *    }
 *  }
 */

// export interface IFormParagraphItem {
//   readonly type: string
// }

// export function isFieldItem(item: IFormParagraphItem): item is FormFieldAtPlaceholder {
//   return (item.type === ParagraphItemType.FIELD)
// }

export interface IFormParagraphTextItem {
  readonly type: 'TEXT'
  readonly markdown: string
  readonly html: string
}

export class FormParagraphTextItem implements IFormParagraphTextItem {
  public readonly type = 'TEXT'
  private _html: string = null

  constructor(private _markdown: string) {
    if (this._markdown == null) {
      this._html = ''
    }
  }

  /**
   * @param What part of the formdownDefinition to extract. From index `from` up to, but not including, `to`
   * @returns The form paragraph which can read the html
   */
  static fromDefinition(formdownDefinition: string, { from, to }: { from: number, to: number }) {
    if (to <= from) {
      return null
    }

    const markdown = formdownDefinition.substring(from, to)

    if (markdown.trim().length === 0) {
      return null
    }

    return new FormParagraphTextItem(markdown)
  }

  get markdown() { return this._markdown }

  get html() {
    if (this._html === null) {
      this._html = markdown(this._markdown)
    }

    return this._html    
  }
}

type FormControlParameters = ControlParameters


export interface IFormStandaloneControl {
  readonly type: 'STANDALONE_CONTROL'

  readonly name: string
  readonly parameters: FormControlParameters
  readonly placeholder: ParsedPlaceholder
}

export class FormStandaloneControl implements IFormStandaloneControl {
  public readonly type = 'STANDALONE_CONTROL'

  private _placeholder: ParsedPlaceholder
  private _parameters: FormControlParameters

  constructor(placeholderInfo: ParsedPlaceholder) {
    this._placeholder = placeholderInfo
    this._parameters = toFormControlParameters(placeholderInfo.control.parameters)
  }

  get name() { return this._placeholder.control.fullName }
  get placeholder() { return this._placeholder }
  get parameters() { return this._parameters }
}

function toFormControlParameters(parameters: ParsedControlParameters): FormControlParameters {  
  const normalizedParams: FormControlParameters = {}

  if (!parameters) {
    return normalizedParams
  }

  for (const key in parameters) {
    const camelCasedKey = camelCase(key)
    normalizedParams[camelCasedKey] = normalizeControlParameterValue(parameters[key].value)
  }

  return normalizedParams
}

function normalizeControlParameterValue(value: any): any {
  if (value === 'true') {
    return true
  }

  if (value === 'false') {
    return false
  }

  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const normalized = {}

    for (const key in value) {
      normalized[camelCase(key)] = normalizeControlParameterValue(value[key])
    }

    return normalized
  }

  return value
}

export interface ParsedStyleParameters {
  [name: string]: {
    value: string | number | boolean
    condition?: {
      id: string,
      expression: string
    }
  }
}

export interface IFormFieldAtPlaceholder {
  readonly type: 'FIELD'
  readonly name: string
  readonly label: string
  readonly controlName: string
  readonly controlParams: ControlParameters
  readonly offset: number
  readonly placeholder: ParsedPlaceholder
}

export class FormFieldAtPlaceholder implements IFormFieldAtPlaceholder {
  public readonly type = 'FIELD'

  private _placeholder: ParsedPlaceholder
  private _controlName: string
  private _controlParameters: ControlParameters

  constructor(placeholderInfo: ParsedPlaceholder) {
    this._placeholder = placeholderInfo
    
    const controlName = placeholderInfo.control?.name || lookupDefaultControlName(placeholderInfo.field)
    this._controlName = placeholderInfo.control?.namespace ? `${placeholderInfo.control.namespace}:${controlName}` : controlName

    this._controlParameters = toFormControlParameters(placeholderInfo.control?.parameters)
  }

  public get offset() {
    return this._placeholder.offset
  }

  public get name() {
    return this._placeholder.field.name
  }

  public get label() {
    return this._placeholder.label
  }

  public get controlName() {
    return this._controlName 
  }

  public get controlParams() {
    return this._controlParameters
  }

  public get placeholder() {
    return this._placeholder
  } 
}

export type IFormParagraphItem = IFormParagraphTextItem | IFormStandaloneControl | IFormFieldAtPlaceholder

export interface IFormSectionParagraph {
  readonly type: string
  readonly items: IFormParagraphItem[]
  readonly offset: number
}

const EMAIL_REGEX = /^[^@\s\t\r\n]+@[^@\s\t\r\n]+\.[^@\s\t\r\n]+$/m

// private _renderToHtml(): string {
//   const offset = this._paragraph.offsetExcludeDirectives
//   const length = this._paragraph.length - (this._paragraph.offsetExcludeDirectives - this._paragraph.offset)
  
//   const markdownInParagraph = this._definition.substring(offset, offset + length)
//   return markdown(markdownInParagraph)
// }

function buildItemsInParsedParagraph(paragraph: any, formDefinition: string) {
  const items: IFormParagraphItem[] = []

  const { hasText } = paragraph
  const paragraphOffset = paragraph.offsetExcludeDirectives
  const paragraphLength = paragraph.length - (paragraph.offsetExcludeDirectives - paragraph.offset)

  const paragraphDefinition = formDefinition.substring(paragraphOffset, paragraphOffset + paragraphLength)

  let offset = 0

  for (const placeholder of paragraph.placeholders) {
    if (hasText) {
      const textItem = FormParagraphTextItem.fromDefinition(paragraphDefinition, { from: offset, to: offset + placeholder.offset} )
      
      if (textItem) {
        items.push(textItem)
      }
    }

    const isField = Boolean(placeholder.field)

    if (isField) {
      items.push(new FormFieldAtPlaceholder(placeholder))
    } else {
      const isControl = Boolean(placeholder.control) 

      if (isControl) {
        const control = new FormStandaloneControl(placeholder)
        items.push(control)
      }
    }
    
    offset += placeholder.length
  }

  if (hasText) {
    const textElement = FormParagraphTextItem.fromDefinition(paragraphDefinition, { from: offset, to: offset + paragraphLength } )
      
    if (textElement) {
      items.push(textElement)
    }
  }

  return items
}

class FormSectionParagraph implements IFormSectionParagraph {
  private _type: 'TEXT' | 'INLINE_WITH_TEXT' | 'INLINE'
  private _paragraph: any
  private _definition: string
  private _items: IFormParagraphItem[] = null

  constructor(paragraph: any, { definition }) {
    this._paragraph = paragraph
    this._definition = definition

    this._type = calculateParagraphType(paragraph)    
  }

  get offset(): number {
    return this._paragraph.offset
  }

  get type() {
    return this._type
  }

  get items(): IFormParagraphItem[] {
    if (this._items === null) {
      this._items = buildItemsInParsedParagraph(this._paragraph, this._definition)
    }

    return this._items
  }
}

let stopOnce = true

function calculateParagraphType(paragraph: any) {
  if (paragraph.hasPlaceholders) {
    if (paragraph.hasText) {
      console.warn('No supported yet')
      return 'INLINE_WITH_TEXT'
    } else {
      return 'INLINE'
    }
  } else if (paragraph.hasText) {
    return 'TEXT'
  }
}

export type FormConditionResults = {
  [conditionId: string]: any
}

export type FormOptionalFieldResults = {
  [fieldName: string]: boolean
}

export type FormFormattingResults = {
  // True if a value was changed due to formatting
  [fieldName: string]: boolean
}

const BUILT_IN_FORMATTER = new FieldFormatter()

export class Form {
  public document: Types.Document

  private _info: ParsedFormInfo = null
  private _fields: FieldInfo[]
  // private _definition: string

  private _formController = null
  private _rootSection: IFormSection = null
  private _temporary = false
  private _typeValidation: Required<TypeValidationOptions>
  private _parseViews = false

  constructor(document: Types.Document, options?: FormOptions) {
    this.document = document
    this._info = null
    this._fields = null
    this._temporary = options?.temporary ?? false
    this._parseViews = options?.parseViews === true
    this._typeValidation = {
      strictTemporalTypes: options?.typeValidation?.strictTemporalTypes !== false,
      dateTimeToDateFieldMode: options?.typeValidation?.dateTimeToDateFieldMode ?? 'preserve',
      timezone: options?.typeValidation?.timezone ?? null,
    }

    if (options && options.version) {
      // this.v = options.version
    }
  }

  get isTemporary() {
    return this._temporary
  }

  compare(form: Form) {
    let c = null

    if (this.document.t && form.document.t) {
      c = this.document.t - form.document.t
    } else {
      c = this.document.created.at.localeCompare(form.document.created.at)
    }

    if (c === 0) {
      c = this.document.v.localeCompare(form.document.v)
    }

    return c
  }

  get allowsUndefinedFields() {
    if ('allowUndefinedFields' in this.document.data) {
      return this.document.data.allowUndefinedFields as boolean
    }

    return false
  }

  get typeValidation(): Required<TypeValidationOptions> {
    return this._typeValidation
  }

  validateFieldValue(fieldSelector: string|ParsedField, value: any) {
    const field = typeof fieldSelector === 'string' ? this.getField(fieldSelector) : fieldSelector
    // TODO: add validation rules ?
    return Form.isMatchingTypeForField(value, field, {
      allowUndefined: this.allowsUndefinedFields,
      strictTemporalTypes: this._typeValidation.strictTemporalTypes,
    })
  }

  static isMatchingTypeForField(value: any, field: FieldInfo, { allowNull = false, allowUndefined = false, strictTemporalTypes = true }: { allowNull?: boolean, allowUndefined?: boolean, strictTemporalTypes?: boolean } = {}) {
    if (value == null && allowNull) {
      return true
    }

    if (value === undefined && allowUndefined) {
      return true
    }

    if (field.cardinality) {
      if (!Array.isArray(value)) {
        if (value != null) {
          return false
        }
      }

      for (const v of value) {
        if (!Form.isMatchingSingleValueForType(v, field.type, field.subType, { allowNull, allowUndefined, strictTemporalTypes })) {
          return false
        }
      }

      return true
    } else {
      return Form.isMatchingSingleValueForType(value, field.type, field.subType, { allowNull, allowUndefined, strictTemporalTypes })
    }
  }

  static isMatchingSingleValueForType(value: any, type: string, subType: string, { allowNull = false, allowUndefined = false, strictTemporalTypes = true }: { allowNull?: boolean, allowUndefined?: boolean, strictTemporalTypes?: boolean }) {
    if (allowNull && value == null) {
      return true
    } 

    if (allowUndefined && value === undefined) {
      return true
    }

    switch (type) {
      case 'string':
        if (subType === 'dayofyear') {
          return isValidDayOfYearString(value)
        }

        if (subType === 'monthofyear') {
          return isValidMonthOfYearString(value)
        }

        if (subType === 'time') {
          return isValidTimeString(value)
        }

        if (subType === 'date') {
          return isValidDateString(value) || (!strictTemporalTypes && isValidDateTimeString(value))
        }

        if (subType === 'datetime') {
          return isValidDateTimeString(value)
        }

        return (typeof value === 'string')
      case 'number':
        if (subType === 'month') {
          return isValidMonth(value)
        }

        if (subType === 'year') {
          return isValidYear(value)
        }

        return (typeof value === 'number')
      case 'boolean':
        return (typeof value === 'boolean')
      case 'object':
        if (typeof value !== 'object' || value === null || Array.isArray(value)) {
          return false
        }

        switch (subType) {
          case 'ref':
            return validateObjectShape(value, REF_SHAPE)
          case 'file':
            return validateObjectShape(value, FILE_SHAPE)
          case 'image':
            return validateObjectShape(value, IMAGE_SHAPE)
          case 'address':
            return validateObjectShape(value, ADDRESS_SHAPE)
          case 'geolocation':
            return validateObjectShape(value, GEOLOCATION_SHAPE)
          case 'currency':
            return validateObjectShape(value, CURRENCY_SHAPE)
          case 'duration':
            return validateObjectShape(value, DURATION_SHAPE)
          default:
            throw new Error(`Unknown subtype ${subType}`)
        }
      default:
        throw new Error(`Unknown type ${type}`)
    }
  }

  validateDocumentData(data: Types.DocumentData): DataValidationErrors {
    if(EditableDocument.isEditableDocument(data)) {
      throw new Error("Invalid usage. Call with data of document")
    }

    const validationErrors: DataValidationErrors = {}

    const conditions = this.evaluateConditions(data)

    for (const field of this.fields) {
      const error = this.validateDocumentDataField(data, field.name, conditions)
      if (error) {
        Object.assign(validationErrors, {
          [field.name]: error
        })
      }
    }

    if (Object.keys(validationErrors).length) {
      return validationErrors
    }
    
    return null
  }
  
  summarizeValidationErrors(data: Types.DocumentData): string {
    const errors = this.validateDocumentData(data)

    if (!errors) {
      return ''
    }

    const fieldNames = Object.keys(errors)
    const descriptions: string[] = []

    for (const fieldName of fieldNames) {
      const fieldErrors = errors[fieldName]
      const placeholder = this.getPlaceholder(fieldName)
      const label = placeholder?.label || fieldName

      if (fieldErrors.required) {
        descriptions.push(`${label} fehlt`)
      } else if (fieldErrors.type) {
        descriptions.push(`${label} ungültig`)
      } else if (fieldErrors.format) {
        descriptions.push(fieldErrors.format.text || `${label} hat falsches Format`)
      } else if (fieldErrors.cardinality) {
        descriptions.push(fieldErrors.cardinality.text || `${label} hat falsche Anzahl`)
      }
    }

    if (descriptions.length === 0) {
      return 'Validierungsfehler'
    }

    if (descriptions.length === 1) {
      return descriptions[0]
    }

    if (descriptions.length <= 3) {
      return descriptions.join(', ')
    }

    return `${descriptions[0]} und ${descriptions.length - 1} weitere Fehler`
  }

  public validateDocumentDataField(data: Types.DocumentData, fieldName: string, conditions?): FieldValidationErrors {
    const fieldInfo = this.getField(fieldName)

    if (!fieldInfo) {
      return null
    }

    const placeholderFieldInfo = this.getPlaceholder(fieldName)
    const label = placeholderFieldInfo?.label || fieldName

    const validationErrors: FieldValidationErrors = {}
    const value = data[fieldName]

    const allConditions = conditions || this.evaluateConditions(data)

    // Skip validation entirely for fields whose condition hides them
    if (this.allowsUndefinedFields && !this.evaluateFieldCondition(fieldInfo, data, allConditions)) {
      return null
    }

    // Required check
    if (fieldInfo.tags.required) {
      const isError = this.fieldHasRequireError(fieldInfo, placeholderFieldInfo, value, allConditions, { data })

      if (isError) {
        const fallbackText = `${label} is required`

        validationErrors.required = {
          text: fieldInfo.settings?.required?.text ?? fallbackText,
          html: fieldInfo.settings?.required?.html ?? fallbackText,
        }
      }
    }

    // Cardinality check — array length within min/max bounds
    if (fieldInfo.cardinality && value != null) {
      const cardinalityError = this._validateCardinality(fieldInfo, value, label)

      if (cardinalityError) {
        validationErrors.cardinality = cardinalityError
      }
    }

    // Type check — value matches expected field type
    if (value != null && value !== undefined) {
      if (!Form.isMatchingTypeForField(value, fieldInfo, {
        allowNull: true,
        allowUndefined: this.allowsUndefinedFields,
        strictTemporalTypes: this._typeValidation.strictTemporalTypes,
      })) {
        validationErrors.type = {
          text: `${label} has an invalid value`,
          html: `${label} has an invalid value`,
        }
      }
    }

    // Bounds apply only to set values of applicable fields: 0 is the empty Number value
    // (see the required check) and a field whose condition is false does not apply.
    if (fieldInfo.type === 'number' && typeof value === 'number' && value !== 0 &&
        this.evaluateFieldCondition(fieldInfo, data, allConditions)) {
      const min = fieldInfo.tags?.min
      const max = fieldInfo.tags?.max
      if (!Number.isFinite(value)) {
        validationErrors.bounds = { text: `${label} must be finite`, html: `${label} must be finite` }
      } else if (min !== undefined && Number.isFinite(Number(min)) && value < Number(min)) {
        const text = `${label} must be at least ${min}`
        validationErrors.bounds = { text, html: text }
      } else if (max !== undefined && Number.isFinite(Number(max)) && value > Number(max)) {
        const text = `${label} must be at most ${max}`
        validationErrors.bounds = { text, html: text }
      }
    }

    // Format validation — check value matches declared format (string fields only)
    if (fieldInfo.tags?.format && value != null && typeof value === 'string' && value !== '') {
      const formatError = this._validateFormat(fieldInfo, value, label)

      if (formatError) {
        validationErrors.format = formatError
      }
    }

    if (Object.keys(validationErrors).length) {
      return validationErrors
    }

    return null
  }

  private _validateCardinality(fieldInfo: FieldInfo, value: any, label: string): FieldValidationError | null {
    if (!Array.isArray(value)) {
      return {
        text: `${label} must be a list`,
        html: `${label} must be a list`,
      }
    }

    const min = typeof fieldInfo.cardinality.min === 'number' ? fieldInfo.cardinality.min : parseInt(fieldInfo.cardinality.min) || 0
    const max = (fieldInfo.cardinality.max as any) === 'n' ? Infinity : (typeof fieldInfo.cardinality.max === 'number' ? fieldInfo.cardinality.max : parseInt(fieldInfo.cardinality.max as any) || Infinity)
    const count = value.length

    if (count < min) {
      const text = min === 1
        ? `${label} requires at least one entry`
        : `${label} requires at least ${min} entries`
      return { text, html: text }
    }

    if (count > max) {
      const text = `${label} allows at most ${max} entries`
      return { text, html: text }
    }

    return null
  }

  private _validateFormat(fieldInfo: FieldInfo, value: string, label: string): FieldValidationError | null {
    const formatTag = fieldInfo.tags.format
    const formatString = typeof formatTag === 'string' ? formatTag : ''

    if (!formatString) {
      return null
    }

    const rules = parseFormatRules(formatString)

    for (const rule of rules) {
      const error = applyFormatRule(rule, value, label)

      if (error) {
        return error
      }
    }

    return null
  }

  public static isFileField(fieldInfo) {
    return (fieldInfo.type === 'object' && (fieldInfo.subType === 'file' || fieldInfo.subType === 'image'))
  }

  public static isMultiField(fieldInfo) {
    return (fieldInfo.cardinality != null)
  }

  public get id() {
    return this.document.id
  }

  public get a() {
    return this.document.a
  }

  public get v() {
    return this.document.v
  }

  public get previous() {
    return this.document.previous
  }

  public get data() {
    return this.document.data
  }

  public get hasIcon() {
    return hasTextIcon(this.document.data.namedIcon)
      || hasTextIcon(this.document.data.iconName)
      || hasTextIcon(this.document.data.iconClass)
      || hasFileIcon(this.document.data.icon)
  }

  public get name() {
    return <string>this.document.data.name
  }

  public get allowedChildrenForms(): Reference[] {
    if (this.document.data.allowedChildrenForms) {
      return <Reference[]> this.document.data.allowedChildrenForms
    }
    return []
  }

  public getRelationRefs(): Types.Reference[] {
    if (Array.isArray(this.document.data.relatedDocuments)) {
      return this.document.data.relatedDocuments as Types.Reference[]
    }

    return []
  }

  public isRelationSource(fieldInfo: FieldInfo): boolean {
    if (fieldInfo?.type !== 'object' || fieldInfo.subType !== 'ref') {
      return false
    }

    return fieldInfo?.referenceSource?.type === 'relation-name' && typeof fieldInfo.referenceSource.name === 'string'
  }

  public async findRelationByName(name: string, aeppic: { getAll: (identifiers: Types.IdOrReference[]) => Promise<Types.Document[]> }): Promise<Types.Document | null> {
    if (!name) {
      return null
    }

    const relationRefs = this.getRelationRefs()
    if (!relationRefs.length) {
      return null
    }

    const relations = (await aeppic.getAll(relationRefs)).filter(Boolean)
    return relations.find(relation => relation?.data?.name === name) ?? null
  }

  private get _definition() {
    return <string>this.document.data.definition
  }

  private _ensureInfo() {
    if (this._info === null) {
      const canonicalInfo = parseForm(this._definition)
      this._info = this._parseViews
        ? withParsedFormViews(canonicalInfo, this.document)
        : canonicalInfo
      this._fields = Object.values(this._info.fields)

      if (this._info.placeholders[0]?.index == null) { 
        log.debug('No index in placeholder', { formId: this.id, formVersion: this.v })
      }

      Object.freeze(this._info)
      Object.freeze(this._fields)
    }
  }

  get rootSection() {
    this._ensureInfo()
    
    if (this._rootSection === null) {
      this._rootSection = new FormSection(this._info.sections[0], this._info)
    }

    return this._rootSection
  }

  *enumerateAllSections(rootSection = this.rootSection): Generator<IFormSection> {
    this._ensureInfo()

    yield rootSection;
    
    for (let subSection of rootSection.subSections) {
      yield * this.enumerateAllSections(subSection)
    }
  }

  /**
   * Return the section ancestry for a field, ordered from root to leaf.
   * Fields declared through conditional or optional sections are included.
   */
  getFieldSectionPath(fieldName: string): IFormSection[] {
    const visit = (section: IFormSection): IFormSection[] | null => {
      const directField = [...this.enumerateAllFieldsInSection(section.index, { skipSubsections: true })]
        .some(field => field.name === fieldName)

      if (directField) {
        return [section]
      }

      for (const child of section.subSections) {
        const childPath = visit(child)
        if (childPath) {
          return [section, ...childPath]
        }
      }

      return null
    }

    return visit(this.rootSection) || []
  }

  getFieldChangeEntries(data: Types.DocumentData, options: FormFieldChangeEntryOptions = {}): FormFieldChangeEntry[] {
    const entries: FormFieldChangeEntry[] = []
    const visited = new Set<string>()
    const conditions = this.evaluateConditions(data)
    const includeHidden = options.includeHidden === true

    const addEntry = (name: string, label: string, sectionPath: string[], sectionIndex: number, placeholderOffset: number, placeholder: ParsedPlaceholder) => {
      if (!name || visited.has(name)) {
        return
      }

      visited.add(name)
      entries.push({
        name,
        label: label || name,
        sectionPath,
        sectionIndex,
        placeholderOffset,
        placeholder,
      })
    }

    const visit = (section: IFormSection, parentPath: string[]) => {
      const sectionTitle = section.title || 'Allgemein'
      const sectionPath = [...parentPath, sectionTitle].filter((title, index, path) => index === 0 || title !== path[index - 1])

      for (const fieldAtPlaceholder of this.enumerateAllFieldsInSection(section.index, { skipSubsections: true })) {
        const placeholder = fieldAtPlaceholder.placeholder
        const field = placeholder?.field

        if (!field?.name) {
          continue
        }

        if (!includeHidden) {
          if (!this.evaluateFieldCondition(field, data, conditions)) {
            continue
          }

          if (_shouldApplyStyleToPlaceholder(placeholder, 'hidden', conditions)) {
            continue
          }
        }

        addEntry(field.name, placeholder.label || field.name, sectionPath, section.index, placeholder.offset ?? Number.MAX_SAFE_INTEGER, placeholder)

        if (placeholder.control?.name === 'optional') {
          const switchFieldName = placeholder.control.parameters?.['switch-field']?.value

          if (typeof switchFieldName === 'string' && switchFieldName) {
            addEntry(switchFieldName, placeholder.label || switchFieldName, sectionPath, section.index, placeholder.offset ?? Number.MAX_SAFE_INTEGER, placeholder)
          }
        }
      }

      for (const child of section.subSections) {
        visit(child, sectionPath)
      }
    }

    visit(this.rootSection, [])

    return entries.sort((left, right) => left.sectionIndex - right.sectionIndex || left.placeholderOffset - right.placeholderOffset)
  }

  get info() {
    this._ensureInfo()
    return this._info
  }

  getViewInfo(name: string): ParsedFormViewInfo | null {
    this._ensureInfo()

    if (!name) {
      return null
    }

    return (this._info as ParsedFormInfoWithViews).views?.[name] ?? null
  }

  hasView(name: string): boolean {
    return this.getViewInfo(name) !== null
  }

  getFormForView(name: string): Form {
    const view = this.getViewInfo(name)

    if (!view) {
      return this
    }

    return new FormViewRenderForm(this, view)
  }

  get fields() {
    this._ensureInfo()
    return this._fields
  }

  getField(name: string) {
    this._ensureInfo()
    return this._info.fields[name] ?? null
  }

  getFieldValidations(fieldSelector: string | FieldInfo): string[] {
    const field = typeof fieldSelector === 'string' ? this.getField(fieldSelector) : fieldSelector
    const tag = field?.tags?.validate

    return normalizeValidationTag(tag)
  }

  getAllFieldValidations(): FieldValidationDeclaration[] {
    return this.fields
      .map(field => ({
        field,
        fieldName: field.name,
        validations: this.getFieldValidations(field),
      }))
      .filter(declaration => declaration.validations.length > 0)
  }

  getFieldLabel(name: string): string {
    const field = this.getField(name)
    if (!field) {
      throw new Error(`Field ${name} is not defined in form ${this.id} (${this.name})`)
    }

    const placeholders = this.getPlaceholders()
    const placeholdersForField = placeholders.filter(p => p.field?.name === name)
    
    // Get first placeholder that has a label
    for (const placeholder of placeholdersForField) {
      if (placeholder.label?.trim().length > 0) {
        return placeholder.label
      }
    }
  }  
  

  getTaggedSections(matchers: TagMatch[], options?: TagMatchOptions ): number[] {
    if (!matchers) {
      throw new Error('matchers is required')
    }
    
    const rootSection = this.info.sections[0]
    
    const self = this

    function fieldMatches(field: ParsedField) {
      return self._fieldTagsMatches(field, matchers, options)
    }

    const undefinedSections = []
    this._checkForMatchingContent(rootSection, null, fieldMatches, undefinedSections)
    return undefinedSections.sort()
  }


  isTaggedSection(document: Types.Document, matchers: TagMatch[], sectionIndex: number, options?: TagMatchOptions): boolean 
  isTaggedSection(document: Types.Document, matchers: TagMatch[], options?: TagMatchOptions): boolean 
  isTaggedSection(document: Types.Document, matchers: TagMatch[], param3: number|TagMatchOptions, param4?: TagMatchOptions): boolean {
    const sectionIndex = typeof(param3) === 'number' ? param3 : 0
    const options = typeof(param3) === 'number' ? param4 : param3

    const section = this.info.sections[sectionIndex]

    if (!section) {
      throw new Error(`Section ${sectionIndex} does not exist in form ${this.id} (${this.name})`)
    }

    const self = this

    function fieldMatches(field: ParsedField) {
      return self._fieldTagsMatches(field, matchers, options)
    }

    const onlyTaggedContent = this._checkForMatchingContent(section, document, fieldMatches)
    return onlyTaggedContent
  }


  /*
   * Return the indices of the sections
   * that are filled with placeholders only 
   * referencing fields that are set to undefined
   * in the specified document.
   * 
   * @param document The document to check
   * @return The indices of the sections that are undefined
   * 
   * This is a recursive check through all sections.
   * A section is undefined if all fields directly and indirectly
   * referenced inside it via placeholders are undefined.
   * 
   */
  getUndefinedSections(document: Types.Document): number[] {
    const rootSection = this.info.sections[0]
    
    const undefinedSections = []
    this._checkForMatchingContent(rootSection, document, (_, value) => value === undefined, undefinedSections)
    return undefinedSections.sort()
  }

  isUndefinedSection(document: Types.Document, sectionIndex: number): boolean {
    const section = this.info.sections[sectionIndex]

    if (!section) {
      throw new Error(`Section ${sectionIndex} does not exist in form ${this.id} (${this.name})`)
    }

    const onlyUndefinedContent = this._checkForMatchingContent(section, document, (_, value) => value === undefined)
    return onlyUndefinedContent
  }

  isSectionDefaultValues(document: Types.Document, sectionIndex: number): boolean {
    const section = this.info.sections[sectionIndex]

    if (!section) {
      throw new Error(`Section ${sectionIndex} does not exist in form ${this.id} (${this.name})`)
    }

    const onlyDefaultValues = this._checkForMatchingContent(section, document, (fieldInfo, value) => this._isDefaultValue(fieldInfo.defaultValue, value))
    return onlyDefaultValues
  }

  isFieldDefaultValue(fieldName: string): boolean {
    const field = this.getField(fieldName)

    if (!field) {
      throw new Error(`Field ${fieldName} is not defined in form ${this.id}`)
    }

    return this._isDefaultValue(field.defaultValue, this.document.data[fieldName])
  }

  _isDefaultValue(defaultValue: any, fieldValue: any) {
    if (typeof defaultValue === 'object') {
      if (typeof fieldValue !== 'object') {
        return false
      }

      for (const key in defaultValue) {
        if (!this._isDefaultValue(defaultValue[key], fieldValue[key])) {
          return false
        }
      }

      return true
    } else if (typeof Array.isArray(defaultValue) ) {
      if (!Array.isArray(fieldValue)) {
        return false
      }

      if (defaultValue.length !== fieldValue.length) {
        return false
      }

      for (let i = 0; i < defaultValue.length; i++) {
        if (!this._isDefaultValue(defaultValue[i], fieldValue[i])) {
          return false
        }
      }

      return true
    } else {
      return defaultValue === fieldValue
    }
  }

  /**
   * Recursively checks for sections where all placeholders match a condition.
   * 
   * A section is considered matching if all fields directly and indirectly
   * referenced inside it via placeholders match the condition.
   * 
   * If a section matches, its index is added to the matchingSections array.
   * If a section has some matching content (directly or indirectly), the method returns true.
   * 
   * By default, sections without any fields are considered non matching unless they 
   * contain only sections which are matching. 
   * 
   * @private
   * 
   * @param section - The section to check for undefined content.
   * @param document - The document containing the parsed form sections.
   * @param matchingSections - An array to store the indices of matching sections.
   * @returns True if the section has only undefined content, false otherwise.
   */
  private _checkForMatchingContent(section: ParsedFormSection, document: Types.Document, condition: (fieldInfo: ParsedField, fieldValue: any) => boolean,  matchingSections: number[] = null) {
    let someSubsectionsHaveContent = false

    for (const directSubSection of section.directSubsections) {
      const sectionOnlyMatchingContent = this._checkForMatchingContent(directSubSection, document, condition, matchingSections)

      if (!sectionOnlyMatchingContent) {
        someSubsectionsHaveContent = true
      }
    }

    // If this section has subsections with content, it is not undefined
    if (someSubsectionsHaveContent) {
      return false
    }
    
    const fieldControlPlaceholders = section.placeholders.filter(placeholder => placeholder.field != null) 

    const hasFields = (fieldControlPlaceholders.length > 0)
    
    let matches = false

    if (!hasFields) {
      const hasSubsections = (section.directSubsections.length > 0)

      // If the section has no fields and no subsections defined
      // it means it is static content. Leave it in and mark it as defined.
      if (!hasSubsections) {
        return false
      }

      // If the section has no fields but has subsections which are
      // all filtered out (see above), it is undefined.
      matches = true
    }

    if (!matches) {
      // If the section has fields, check if they are all undefined
      const allFieldsMatch = this._allFieldsReferencedViaPlaceholdersMatch(section, document, condition)
      matches = allFieldsMatch
    }

    if (matches) {
      matchingSections?.push(section.index)  
    }

    return matches
  }

  private _allFieldsReferencedViaPlaceholdersMatch(section: ParsedFormSection, document: Types.Document, condition: (fieldInfo: ParsedField, fieldValue: any) => boolean) {
    for (const placeholder of section.placeholders) {
      if (placeholder.field == null) {
        continue
      }

      const fieldName = placeholder.field.name
      const field = this.getField(fieldName)

      if (field == null) {
        console.warn('Field not found', fieldName, placeholder, 'section index', section.index)
      }
      
      const match = condition(field, document?.data[fieldName])

      if (!match) {
        return false
      }
    }
    
    return true
  }

  isUndefinedField(document: Types.Document, fieldName: string) {
    const field = this.getField(fieldName)

    if (!field) {
      throw new Error(`Field ${fieldName} is not defined in form ${this.id}`)
    }

    return document.data[fieldName] === undefined
  }

  *enumerateAllFieldsInSection(sectionIndex: number, { skipSubsections }: { skipSubsections?: boolean } = {}) {
    const section = this.info.sections[sectionIndex]

    if (!section) {
      throw new Error(`Section ${sectionIndex} is not defined in form ${this.id}`)
    }

    yield *this._enumerateDirectFieldsInSection(section)
    
    if (skipSubsections) {
      return
    }

    for (const { index } of section.directSubsections) {
      yield *this.enumerateAllFieldsInSection(index)
    }
  }

  private *_enumerateDirectFieldsInSection(section: ParsedFormSection) {
    if (!section.placeholders) {
      return 
    }

    for (const placeholder of section.placeholders) {
      const fieldName = placeholder.field?.name

      if (fieldName) {
        const field = this.getField(fieldName)
        yield { 
          ...field,
          label: placeholder.label,
          placeholder,
        }
      }
    }
  }

  isTaggedField(field: string|FieldInfo, matchers: TagMatch[], options: TagMatchOptions = {}) {
    if (!field) {
      throw new Error('Field is required')
    }

    if (!matchers) {
      throw new Error('Matchers are required')
    }

    if (typeof field === 'string') {
      field = this.getField(field)
    
      if (!field) {
        throw new Error(`Field ${field} is not defined in form ${this.id}`)
      }
    }

    return this._fieldTagsMatches(field, matchers, options)
  }

  hasTaggedFields(matchers: TagMatch[]): boolean {
    const taggedFields = this.getTaggedFields(matchers)
    return taggedFields.length > 0
  }
  
  getTaggedFields(matchers: TagMatch[], sectionIndex?: number): string[]
  getTaggedFields(matchers: TagMatch[], options?: TagMatchOptions): string[] 
  getTaggedFields(matchers: TagMatch[], param2?: number|TagMatchOptions, param3?: TagMatchOptions): string[] {
    this._ensureInfo()

    const sectionIndex = (typeof param2 === 'number') ? param2 : undefined
    const options = (typeof param2 === 'object') ? param2 : param3

    const self = this
    
    function fieldMatches(field: ParsedField) {
      return self._fieldTagsMatches(field, matchers, options)
    }

    return this._findDocumentFields(null, fieldMatches, sectionIndex)
  }

  private _fieldTagsMatches(field: ParsedField, matchers: TagMatch[], options: TagMatchOptions = {}) {
    if (!field) {
      throw new Error('Field is required')
    }

    if (matchers.length === 0) {
      return true
    }

    if (!field.tags) {
      return false
    }

    const DEFAULT_FIELD_MATCH_OPTIONS = { operation: 'and' }
    const { operation: operation } = { ...DEFAULT_FIELD_MATCH_OPTIONS, ...options }
    
    const defaultResult = (operation === 'and') ? true : false

    for (const matcher of matchers) {
      // extract tag match and value match from matcher 
      const matchInfo = this._extractTagMatchingInfo(matcher)
      const matches = Object.entries(field.tags).some(([tagName, tagValue]) => this._tagMatchesTagMatchInfo(tagName, tagValue, matchInfo))
      
      if (operation === 'and' && !matches) {
        return false
      }

      if (operation === 'or' && matches) {
        return true
      }
    }

    return defaultResult
  }

  _tagMatchesTagMatchInfo(tagName: string, tagValue: ParsedTag, matchInfo: TagMatchInfo) {
    const { name, value, matchName, matchValue } = matchInfo

    if (name && tagName !== name) {
      return false
    }

    if (value && tagValue !== value) {
      return false
    }

    if (matchName && !matchName.test(tagName)) {
      return false
    }

    if (matchValue) {
      if (typeof tagValue === 'boolean') {
        return false
      }

      // Handle expression tags (they do not match regular expressions)
      if (typeof tagValue === 'object') {
        return false
      }

      if (typeof tagValue === 'string') {
        if (!matchValue.test(tagValue)) {
          return false
        }
      }
    }

    return true
  }

  _extractTagMatchingInfo(matcher: TagMatch) {
    // if matcher is regexp
    if (matcher instanceof RegExp) {
      return { matchName: matcher }
    } else if (typeof matcher === 'string') {
      return { name: matcher}
    } else if (typeof matcher === 'object') {
      let matchInfo: TagMatchInfo = {}

      if (typeof matcher.name === 'string') {
        matchInfo.name = matcher.name
      } else if (matcher.name instanceof RegExp) {
        matchInfo.matchName = matcher.name
      }

      if (typeof matcher.value === 'string') {
        matchInfo.value = matcher.value
      } else if (matcher.value instanceof RegExp) {
        matchInfo.matchValue = matcher.value
      }

      return matchInfo 
    } else {
      throw new Error(`Invalid tag matcher ${JSON.stringify(matcher)}`)
    }
  }

  getUndefinedFields(document: Types.Document, sectionIndex?: number) {
    return this._findDocumentFields(document, (_, value) => value === undefined, sectionIndex)
  }

  private _findDocumentFields(document: Types.Document, condition: (field: ParsedField, value: any) => boolean, sectionIndex?: number) {
    const matching: string[] = []
    
    if (document) {
      if (document.f.id !== this.id) {
        throw new Error(`Document form ${document.f.id} is not this form`)
      }

      if (document.f.v !== this.v) {
        throw new Error(`Document form ${document.f.id} version ${document.f.v} is not this form version ${this.v}`)
      }
    }

    let fields: [string, FieldInfo, any][] = []

    if (sectionIndex) {
      fields = [...this.enumerateAllFieldsInSection(sectionIndex)].map(field => [field.name, field, document?.data[field.name]])
    } else {
      fields = Array.from(this._fields.values()).map(field => [field.name, field, document?.data[field.name]])
    }

    for (const [fieldName, fieldInfo, fieldValue] of fields) {
      if (!fieldInfo) {
        throw new Error(`Document field ${fieldName} is not defined in form ${this.id}`)
      }
      
      if (condition(fieldInfo, fieldValue)) {
        matching.push(fieldName)
      }      
    }
    
    return matching
  }

  getPlaceholders({ includeFieldDeclarations: includePureFieldDeclarations = false } = {}): ParsedPlaceholder[] {
    if (includePureFieldDeclarations) {
      return this.info.placeholders
    } else {
      return this.info.placeholders.filter(p => !(p.field == null && p.control == null))
    }
  }

  getPlaceholder(fieldName: string): ParsedPlaceholder {
    return this.info.placeholders.find(p => p.field && p.field.name === fieldName)
  }

  private _preloadedControls = new Map<string, { controls: Map<number, Types.Document | null>, identity?: string }>()

  clearPreloadedControls() { this._preloadedControls.clear() }

  setPreloadedControls(controls: Map<number, Types.Document | null>, variant: ControlLookupVariant = {}, identity?: string) {
    this._preloadedControls.set(JSON.stringify(normalizeControlLookupVariant(variant)), { controls, identity })
  }

  getPreloadedControl(placeholder: number | ParsedPlaceholder, variant: ControlLookupVariant = {}, identity?: string): Types.Document | null | undefined {
    const entry = this._preloadedControls.get(JSON.stringify(normalizeControlLookupVariant(variant)))
    if (!entry || (identity !== undefined && entry.identity !== identity)) return undefined
    const offset = typeof placeholder === 'number' ? placeholder : placeholder?.offset
    return entry.controls.get(offset)
  }

  getControlForPlaceholder(placeholder: number | ParsedPlaceholder, variant: ControlLookupVariant = {}): Types.Document | null | undefined {
    return this.getPreloadedControl(placeholder, variant)
  }

  getPlaceholderControlParams(placeholderIndexOrName: string|number) {
    let placeholder

    if (typeof placeholderIndexOrName === 'number') {
      placeholder = this.info.placeholders[placeholderIndexOrName]
    } else {
      placeholder = this.getPlaceholder(placeholderIndexOrName)
    }

    if (!placeholder?.control?.parameters) {
      return null
    }
    
    return toFormControlParameters(placeholder.control.parameters)
  }

  getFieldControlParams(fieldName) {
    const placeholderFieldInfo = this.getPlaceholder(fieldName)

    if (placeholderFieldInfo && placeholderFieldInfo.control && placeholderFieldInfo.control.parameters) {
      return toFormControlParameters(placeholderFieldInfo.control.parameters)
    }

    return null
  }
  
  hasField(name: string): boolean {
    return !!this.getField(name)
  }

  hasCalculations(): boolean {
    const info = this.info

    if (Object.keys(info.conditions).length) {
      return true
    }

    for (const fieldName in info.fields) {
      if (this.fieldHasFormula(fieldName)) {
        return true
      }
    }

    return false
  }

  hasFormatting(): boolean {
    const info = this.info

    for (const fieldName in info.fields) {
      if (this.fieldHasFormatting(fieldName)) {
        return true
      }
    }

    return false
  }

  hasNormalization(): boolean {
    const info = this.info

    for (const fieldName in info.fields) {
      if (this.fieldHasNormalization(fieldName)) {
        return true
      }
    }

    return false
  }

  hasOptionalFields({ expressionsOnly = false } = {}): boolean {
    if (!this.allowsUndefinedFields) {
      return false
    }
    
    if (expressionsOnly) {
      return this.fields.some(f => f.condition || fieldTagHasExpression(f.tags?.optional))
    } else {
      return this.fields.some(f => f.condition || f.tags?.optional)
    }
  }

  getFormController() {
    // if (!this.document.data.functions)
    //   return null

    if (!this._formController) {
      const codeGroup = 'forms'
      const name = `${this.name}(${this.v})`
      const functions = this.document.data.functions ? this.document.data.functions.toString() : ''

      const controllerClassCode = `class Controller{\n`
                                + `${this.indentCode(functions)}\n\n`
                                + `${this.indentCode(this.createConditionsCode())}\n\n`
                                + `${this.indentCode(this.createFormulaCode())}\n\n`
                                + `${this.indentCode(this.createFormattingCode())}\n\n`
                                + `${this.indentCode(this.createNormalizationCode())}\n\n`
                                + `${this.indentCode(this.createFieldExpressionsCode())}\n\n`
                                + `}`
      
      const variablesToExpose = []
      const globals = []

      const FormControllerClass = createControllerClass(codeGroup, name, controllerClassCode, variablesToExpose, globals)
      this._formController = new FormControllerClass([])
    }

    return this._formController
  }

  indentCode(code) {
    return '  ' + code.split('\n').join('\n  ')
  }

  createConditionsCode() {
    let code = ''

    for (const conditionId in this.info.conditions ) {
      const conditionExpression = this.info.conditions[conditionId]
      code += this.createFormulaFunctionCode(conditionId, conditionExpression)
    }

    return code
  }

  createFormulaCode() {
    let code = ''

    const fields = this.fields.filter(f => f.settings && f.settings.formula)

    for (const field of fields) {
      code += this.createFormulaFunctionCode(field.name, field.settings.formula)
    }

    return code
  }

  createFormulaFunctionCode(name, formulaExpression) {
    const dataFieldsAsParameters = Object.keys(this.info.fields).map(key => this.escapeReservedWord(key))
    const allParameters = [...dataFieldsAsParameters, 'functions', 'Math', 'DateTime', 'DateTimeDuration']

    const code = `_formula_${name}(${allParameters.join(', ')}){\n`
               + `  return ${formulaExpression}\n`
               + `}\n\n`

    return code
  }

  createFormattingCode() {
    let code = ''

    const fields = this.fields.filter(f => f.tags?.sanitize)

    for (const field of fields ) {
      code += this.createFormattingFunctionCode(field.name, field.tags.sanitize)
    }

    return code
  }

  createNormalizationCode() {
    let code = ''

    const fields = this.fields.filter(f => f.tags?.normalize)

    for (const field of fields ) {
      code += this.createNormalizationFunctionCode(field.name, field.tags.normalize)
    }

    return code
  }

  createFieldExpressionsCode() {
    let code = ''
    
    for (const field of this.fields) {
      if (this.allowsUndefinedFields && fieldTagHasExpression(field.tags?.optional)) {
        code += this.createFieldFunctionCode(field.name, 'optional', field.tags.optional.expression)
      }
      if (fieldTagHasExpression(field.tags?.required)) {
        code += this.createFieldFunctionCode(field.name, 'required', field.tags.required.expression)
      }
    }

    return code
  }

  createFormattingFunctionCode(name, formattingExpression) {
    const field = this.getField(name)

    if (field.type !== 'string') {
      console.warn(`Field ${name} is not a string field and cannot be formatted`)
      return 
    }

    const dataFieldsAsParameters = Object.keys(this.info.fields).map(key => this.escapeReservedWord(key))
    const allParameters = [...dataFieldsAsParameters, 'functions', 'Math', 'DateTime', 'DateTimeDuration', 'builtInFormats']

    const steps = createFormattingStepCodes(formattingExpression, {
      onUnknownFunction: (functionName) => console.warn(`Unknown formatting function ${functionName} in field ${name}`)
    })

    const code = `_format_${name}(${allParameters.join(', ')}){\n`
               + `  let current = ${name};\n`
               + `  current = current == null ? '' : String(current);\n`
              +  `  ${steps.join(';\n')}\n`
               + `  return current;\n`
               + `}\n\n`

    return code
  }

  createNormalizationFunctionCode(name, normalizationExpression) {
    const field = this.getField(name)

    if (field.type !== 'string') {
      console.warn(`Field ${name} is not a string field and cannot be normalized`)
      return 
    }

    const dataFieldsAsParameters = Object.keys(this.info.fields).map(key => this.escapeReservedWord(key))
    const allParameters = [...dataFieldsAsParameters, 'functions', 'Math', 'DateTime', 'DateTimeDuration', 'builtInFormats']

    const steps = createFormattingStepCodes(normalizationExpression, {
      onUnknownFunction: (functionName) => console.warn(`Unknown normalization function ${functionName} in field ${name}`)
    })

    const code = `_normalize_${name}(${allParameters.join(', ')}){\n`
               + `  let current = ${name};\n`
               + `  current = current == null ? '' : String(current);\n`
              +  `  ${steps.join(';\n')}\n`
               + `  return current;\n`
               + `}\n\n`

    return code
  }

  getFieldFunctionName(name: string, prefix: 'optional'|'required'): string {
    return `_${prefix}_${name}`
  }

  createFieldFunctionCode(name: string, prefix: 'optional'|'required', requirementExpression) {
    const dataFieldsAsParameters = Object.keys(this.info.fields).map(key => this.escapeReservedWord(key))
    const allParameters = [...dataFieldsAsParameters, 'functions', 'Math', 'DateTime', 'DateTimeDuration']

    const functionName = this.getFieldFunctionName(name, prefix)
    const code = `${functionName}(${allParameters.join(', ')}){\n`
               + `  return ${requirementExpression}\n`
               + `}\n\n`

    return code
  }
  
  escapeReservedWord(word) {
    const reservedWords = ['abstract', 'arguments', 'await', 'async', 'boolean', 'break', 'byte', 'case', 'catch', 'char',
                           'class', 'const', 'continue', 'debugger', 'default', 'delete', 'do', 'double', 'else',
                           'enum', 'eval', 'export', 'extends', 'false', 'final', 'finally', 'float', 'for',
                           'function', 'goto', 'if', 'implements', 'import', 'in', 'instanceof', 'int', 'interface',
                           'let', 'long', 'native', 'new', 'null', 'package', 'private', 'protected', 'public',
                           'return', 'short', 'static', 'super', 'switch', 'synchronized', 'this', 'throw', 'throws',
                           'transient', 'true', 'try', 'typeof', 'var', 'void', 'volatile', 'while', 'with', 'yield']

    reservedWords.push(...['functions', 'Math', 'DateTime', 'DateTimeDuration'])                       
    
    return (reservedWords.indexOf(word) >= 0 ? `$${word}` : word)
  }

  evaluateFormula(formulaName, data) {
    const ctrl = this.getFormController()
    const dataFieldsAsParameters = Object.keys(this.info.fields)
    const fields = dataFieldsAsParameters.map( (fieldName) => data[fieldName] )
    const parameters = [...fields, ctrl, Math, DateTime, DateTimeDuration]

    const formulaFunction = ctrl[`_formula_${formulaName}`]
    return formulaFunction.apply(ctrl, parameters)
  }

  evaluateConditions(data: Types.DocumentData): FormConditionResults {
    const result: FormConditionResults = {}

    for (const conditionId in this.info.conditions ) {
      result[conditionId] = !!(this.evaluateFormula(conditionId, data))
    }

    return result
  }  

  applyOptionalFieldRules(data: Types.DocumentData): FormOptionalFieldResults|null {
    if (!this.allowsUndefinedFields) {
      return null
    }

    let changed = false
    
    const results = {}
    
    for (const field of this.fields) {
      let canBeUndefined = false
      let shouldApplyRule = false

      if (field.condition) {
        shouldApplyRule = true

        const isApplicable = this.evaluateFieldCondition(field, data)
        canBeUndefined = !isApplicable

        if (!isApplicable) {
          results[field.name] = canBeUndefined

          if (data[field.name] !== undefined) {
            data[field.name] = undefined
            changed = true
          }

          continue
        }
      }

      if (fieldTagHasExpression(field.tags?.optional)) {
        shouldApplyRule = true
        canBeUndefined = !!this.evaluateFieldTagExpression(field.name, 'optional', data)
      } else if (field.tags?.optional) {
        shouldApplyRule = true
        canBeUndefined = true
      }

      if (shouldApplyRule) {
        results[field.name] = canBeUndefined

        if (canBeUndefined && field.condition && data[field.name] === this.createDefaultFieldValue(field.name)) {
          data[field.name] = undefined
          changed = true
        } else if (!canBeUndefined && data[field.name] === undefined) {
          data[field.name] = this.createDefaultFieldValue(field.name)
          changed = true
        }
      } else if (data[field.name] === undefined) {
        // Field has no <if> condition and no <optional> tag, so it can
        // never legitimately be undefined.  Repair with default value.
        data[field.name] = this.createDefaultFieldValue(field.name)
        results[field.name] = false
        changed = true
      }
    }

    if (!changed) {
      return null
    } else {
      return results
    }
  }

  recalculateData(data: Types.DocumentData) {
    const optionalFieldChanges = this.applyOptionalFieldRules(data)
    const calculatedFieldsChanged = this.updateCalculatedFields(data)
    const formattingChanges = this.applyFormatting(data)

    return {
      optionalFieldChanges,
      calculatedFieldsChanged,
      formattingChanges,
    }
  }

  applyFormatting(data: Types.DocumentData): FormFormattingResults|null {
    let changed = false
    
    const results = {}
    
    for (const field of this.fields) {
      if (this.canApplyFieldFormatting(field)) {
        const previous = data[field.name]
        data[field.name] = this.applyFieldFormatting(field.name, data)

        if (previous !== data[field.name]) {
          results[field.name] = true
          changed = true
        }
      }
    }

    if (!changed) {
      return null
    } else {
      return results
    }
  }
  
  applyFieldFormatting(fieldName: string, data: Types.DocumentData, fieldValue = data[fieldName]) {
    const ctrl = this.getFormController()
    const dataFieldsAsParameters = Object.keys(this.info.fields)
    const fields = dataFieldsAsParameters.map( (currentFieldName) => currentFieldName === fieldName ? fieldValue : data[currentFieldName] )
    // const allParameters = [...dataFieldsAsParameters, 'functions', 'Math', 'DateTime', 'DateTimeDuration', 'builtInFormats']
    const parameters = [...fields, ctrl, Math, DateTime, DateTimeDuration, BUILT_IN_FORMATTER]
    const formulaFunction = ctrl[`_format_${fieldName}`]
    return formulaFunction.apply(ctrl, parameters)
  }

  applyNormalization(data: Types.DocumentData): FormFormattingResults|null {
    let changed = false
    
    const results = {}
    
    for (const field of this.fields) {
      if (this.canApplyFieldNormalization(field)) {
        const previous = data[field.name]
        data[field.name] = this.applyFieldNormalization(field.name, data)

        if (previous !== data[field.name]) {
          results[field.name] = true
          changed = true
        }
      }
    }

    if (!changed) {
      return null
    } else {
      return results
    }
  }
  
  applyFieldNormalization(fieldName: string, data: Types.DocumentData, fieldValue = data[fieldName]) {
    const ctrl = this.getFormController()
    const dataFieldsAsParameters = Object.keys(this.info.fields)
    const fields = dataFieldsAsParameters.map( (currentFieldName) => currentFieldName === fieldName ? fieldValue : data[currentFieldName] )
    const parameters = [...fields, ctrl, Math, DateTime, DateTimeDuration, BUILT_IN_FORMATTER]
    const normalizationFunction = ctrl[`_normalize_${fieldName}`]
    return normalizationFunction.apply(ctrl, parameters)
  }

  evaluateFieldTagExpression(fieldName: string, tag: 'optional'|'required', data: Types.DocumentData): boolean {
    const ctrl = this.getFormController()
    const dataFieldsAsParameters = Object.keys(this.info.fields)
    const fields = dataFieldsAsParameters.map( (fieldName) => data[fieldName] )
    // const allParameters = [...dataFieldsAsParameters, 'functions', 'Math', 'DateTime', 'DateTimeDuration', 'builtInFormats']
    const parameters = [...fields, ctrl, Math, DateTime, DateTimeDuration, BUILT_IN_FORMATTER]
    const functionName = this.getFieldFunctionName(fieldName, tag)
    const optionalFunction = ctrl[functionName]
    return optionalFunction.apply(ctrl, parameters)
  }

  private evaluateFieldCondition(field: ParsedField, data: Types.DocumentData, conditions?: FormConditionResults): boolean {
    if (!field.condition) {
      return true
    }

    if (conditions && field.condition.id in conditions) {
      return !!conditions[field.condition.id]
    }

    return !!this.evaluateFormula(field.condition.id, data)
  }

  private fieldHasRequireError(fieldInfo, placeholder, value, conditions, { ignoreCardinality = false, data }: { ignoreCardinality?: boolean, data?: Types.DocumentData }) {
    // NOTE: only one/first placeholder is checked. This needs to be improved in the future
    // 'hidden' precedes required 
    if (_shouldApplyStyleToPlaceholder(placeholder, 'hidden', conditions)) {
      return false
    }

    // 'optional' or precedes required
    if (value === null || value === undefined) {
      return true
    }

    // Is the require expression itself linked to an expression ? If so evaluate it
    let requiredExpression = fieldTagHasExpression(fieldInfo.tags?.required) 

    if (requiredExpression && data) {
      const isRequired = this.evaluateFieldTagExpression(fieldInfo.name, 'required', data)

      if (!isRequired) {
        return false
      }
    }
    
    if (!ignoreCardinality && fieldInfo.cardinality) {
      if (!Array.isArray(value) || value.length === 0) {
        return true
      }
      
      return value.some(v => this.fieldHasRequireError(fieldInfo, placeholder, v, conditions, { ignoreCardinality: true }))
    }
    
    switch (fieldInfo.type) {
      case 'string': {
        switch (fieldInfo.subType) {
          case 'mail':
            return !value || !value.match(EMAIL_REGEX)
        }

        return !value
      }
      case 'number': {
        return Number.isNaN(value) || value === 0
      }
      case 'boolean': {
        return !value
      }
      case 'object':
        switch (fieldInfo.subType) {
          case 'ref':
            return !('id' in value) ||
                    value.id === null || typeof(value.id) !== 'string' || value.id === '' ||
                    !('v' in value) ||
                    value.v === null || typeof(value.v) !== 'string' || value.v === ''
            // TODO: Also check for text ?
          case 'file':
          case 'image':
            return !('dataUrl' in value) ||
                    value.dataUrl === null || typeof(value.dataUrl) !== 'string' || value.dataUrl === ''
            // TODO: Also check for all other members ?
          case 'address':
            return  !('city' in value) ||
                    value.city === null || typeof(value.city) !== 'string' || value.city === ''
            // TODO: Also check for all other members ?
          case 'geolocation':
            return  !('lat' in value) ||
                    value.lat === null || typeof(value.lat) !== 'number' ||
                    !('lon' in value) ||
                    value.lon === null || typeof(value.lon) !== 'number'
          case 'duration':
            const keys = Object.keys(value)

            if (keys.length === 0) {
              return true
            } else {
              const firstMissingValue = keys.find(k => value[k] == null)
              return !!firstMissingValue
            }
          case 'currency': 
            return  !('amount' in value) || typeof(value.amount) !== 'number' || value.amount === 0 ||
                    !('currency' in value) || typeof(value.currency) !== 'string' || value.currency === '' ||
                    !('precision' in value) || typeof(value.precision) !== 'number'
      }
    }
  }
  
  shouldApplyStyleToPlaceholder(placeholder: ParsedPlaceholder, styleName: string, evaluatedConditions: any): boolean {
    return _shouldApplyStyleToPlaceholder(placeholder, styleName, evaluatedConditions)
  }

  shouldApplyStyleToSection(section, styleName: string, evaluatedConditions: any): boolean {
    // currently compatible....
    return _shouldApplyStyleToPlaceholder(section, styleName, evaluatedConditions)
  }
  
  updateCalculatedFields(data: any): boolean {
    const MAX_ITERATIONS = 20
    const fieldsWithFormulas = this.getFieldsWithFormulas()
    const ctrl = this.getFormController()

    for (let iteration = 0; iteration < MAX_ITERATIONS; iteration += 1) {
      let didChangeDataInThisIteration = false

      for (const field of fieldsWithFormulas) {    
        const previousFieldValue = data[field.name]
        let newFieldValue = this.evaluateFormula(field.name, data)

        if (this.canApplyFieldFormatting(field)) {
          newFieldValue = this.applyFieldFormatting(field.name, data, newFieldValue)
        }

        if (this.canApplyFieldNormalization(field)) {
          newFieldValue = this.applyFieldNormalization(field.name, data, newFieldValue)
        }

        if (fieldHasChanged(field, previousFieldValue, newFieldValue) ) {
          data[field.name] = newFieldValue
          didChangeDataInThisIteration = true
        }
      }

      if (!didChangeDataInThisIteration) {
        return (iteration > 0)
      }
    }

    console.warn('Too many iterations calculating formulas.')
    return true
  }

  private getFieldsWithFormulas() {
    return this.fields.filter(f => f.settings && f.settings.formula)
  }

  private fieldHasFormula(fieldName: string) {
    const field = this.getField(fieldName)
    return field?.settings?.formula
  }

  private fieldHasFormatting(fieldName: string) {
    const field = this.getField(fieldName)
    return typeof field?.tags?.sanitize === 'string'
  }

  private fieldHasNormalization(fieldName: string) {
    const field = this.getField(fieldName)
    return typeof field?.tags?.normalize === 'string'
  }

  private canApplyFieldFormatting(field: FieldInfo) {
    return field?.subType === 'text' && typeof field.tags?.sanitize === 'string'
  }

  private canApplyFieldNormalization(field: FieldInfo) {
    return field?.subType === 'text' && typeof field.tags?.normalize === 'string'
  }

  freezeData(data) {
    Object.freeze(data)

    // Note: Don't use .fields since this
    // would force parsing of forms on import
    //
    // If they exist nice, otherwise just walk through
    // using vanilla JS
    //
    if (this._fields) {
      for (const fieldInfo of this._fields) {
        if (fieldInfo.type === 'object') {
          Object.freeze(data[fieldInfo.name])
        }
      }
    } else {
      for (const dataPropertyValue of Object.values(data)) {
        Object.freeze(dataPropertyValue)
      }
    }
  }

  cloneDocumentData(data, fieldsNotToClone?: Array<string>) {
    const clone = {}

    for (const fieldInfo of this.fields) {
      const cloneFromDefault = !!(fieldsNotToClone && fieldsNotToClone.includes(fieldInfo.name))
      
      const fieldData = cloneFromDefault ? null : data[fieldInfo.name]

      clone[fieldInfo.name] = this.cloneFieldValue(fieldData, fieldInfo.name, cloneFromDefault)
    }

    return clone
  }

  cloneDocumentField(data, fieldName, cloneFromDefault?: boolean) {
    const value = data[fieldName]
    return this.cloneFieldValue(value, fieldName, cloneFromDefault)
  }
  
  cloneFieldValue(value, fieldName, cloneFromDefault?: boolean) {
    if (!cloneFromDefault && typeof value === 'undefined') { 
      return undefined
    }

    let normalizedValue = value

    if (cloneFromDefault === true && value == null) {
      if (this.info.fields[fieldName].cardinality) {
        normalizedValue = []
      } else {
        normalizedValue = this.info.fields[fieldName].defaultValue
      }
    }
    
    return Form.cloneFieldValue(this.info.fields[fieldName], normalizedValue)
  }

  createDefaultFieldValue(fieldName: string) {
    const fieldInfo = this.getField(fieldName)

    if (this.info.fields[fieldName].cardinality) {
      return []
    } else {
      return Form.cloneSingleFieldValue(fieldInfo, fieldInfo.defaultValue)
    }
  }

  copyFieldsFromDifferentForm(data, previousData, previousForm: Form) {
    for (const field of this._fields) {
      const previousFieldDefinition = previousForm.getField(field.name)

      if (field.name in previousData && previousFieldDefinition) {
        data[field.name] = copyFieldFromDifferentForm(data[field.name], field, previousData[field.name], previousFieldDefinition)
      }
    }
  }

  static isRefField(fieldInfo) {
    return fieldInfo && fieldInfo.type === 'object' && fieldInfo.subType === 'ref'
  }

  static cloneFieldValue(fieldInfo, field) {
    if (fieldInfo.cardinality) {
      return cloneArrayOfValues(fieldInfo, field)
    } else {
      return cloneSingleField(fieldInfo, field)
    }
  }

  static cloneSingleFieldValue(fieldInfo, field) {
    return cloneSingleField(fieldInfo, field)
  }

  hasChanged(fromData, toData) {
    const info = this.info

    for (const fieldName in info.fields) {
      const fieldInfo = info.fields[fieldName]

      if (!fieldInfo) {
        continue
      }

      if (Form.hasFieldChanged(fieldInfo, fromData[fieldName], toData[fieldName])) {
        return true
      }
    }

    return false
  }

  hasFieldChanged(fieldInfo, from, to, options?) {
    return Form.hasFieldChanged(fieldInfo, from, to, options)
  }
  
  static hasSingleValueChanged(fieldInfo, from, to) {
    return singleValueHasChanged(fieldInfo, from, to)
  }

  static hasFieldChanged(fieldInfo, from, to, options?) {
    if (fieldHasChanged(fieldInfo, from, to, options)) {
      return true
    } else {
      return false
    }
  }
}

function isValidDayOfYearString(value: any) {
  if (typeof value !== 'string') {
    return false
  }

  if (value === '') {
    return true
  }

  const match = /^--(\d{2})-(\d{2})$/.exec(value)

  if (!match) {
    return false
  }

  const month = Number(match[1])
  const day = Number(match[2])
  const daysInMonth = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]

  return month >= 1 && month <= 12 && day >= 1 && day <= daysInMonth[month - 1]
}

function isValidMonthOfYearString(value: any) {
  if (typeof value !== 'string') {
    return false
  }

  if (value === '') {
    return true
  }

  const match = /^(\d{4})-(\d{2})$/.exec(value)

  if (!match) {
    return false
  }

  const month = Number(match[2])

  return month >= 1 && month <= 12
}

function isValidMonth(value: any) {
  if (typeof value !== 'number') {
    return false
  }

  return Number.isInteger(value) && value >= 1 && value <= 12
}

function isValidYear(value: any) {
  if (typeof value !== 'number') {
    return false
  }

  return Number.isInteger(value) && value >= 1 && value <= 9999
}

// -- Object field shape definitions and validation --
//
// Each shape field is either:
//   { type, required }  — a leaf field
//   { shape, required } — a nested object
//
// `type` values: 'string' | 'number' | 'boolean' | 'nullable-string' | 'nullable-number'
// `required`: true  — must be present in the value, validation fails if missing
//             false — may be omitted, will be filled from default during normalization

type FieldType = 'string' | 'number' | 'boolean' | 'nullable-string' | 'nullable-number'

interface ShapeLeaf {
  type: FieldType
  required: boolean
}

interface ShapeNested {
  shape: ObjectShape
  required: boolean
}

type ShapeEntry = ShapeLeaf | ShapeNested
type ObjectShape = Record<string, ShapeEntry>

function req(type: FieldType): ShapeLeaf { return { type, required: true } }
function opt(type: FieldType): ShapeLeaf { return { type, required: false } }
function reqShape(shape: ObjectShape): ShapeNested { return { shape, required: true } }
function optShape(shape: ObjectShape): ShapeNested { return { shape, required: false } }

const REF_SHAPE: ObjectShape = {
  id:      req('nullable-string'),
  v:       req('nullable-string'),
  text:    opt('string'),
  // Optional usage metadata set by the referencing document (see Types.Reference)
  alias:   opt('string'),
  comment: opt('string'),
}

const FILE_INFO_SHAPE: ObjectShape = {
  created:  opt('nullable-number'),
  read:     opt('nullable-number'),
  modified: opt('nullable-number'),
  imported: opt('nullable-number'),
}

const FILE_SHAPE: ObjectShape = {
  dataUrl:      req('nullable-string'),
  name:         opt('nullable-string'),
  size:         opt('nullable-number'),
  type:         opt('nullable-string'),
  sha1:         opt('nullable-string'),
  thumbnailUrl: opt('nullable-string'),
  iconUrl:      opt('nullable-string'),
  previewUrl:   opt('nullable-string'),
  fileInfo:     optShape(FILE_INFO_SHAPE),
}

const MEDIA_INFO_SHAPE: ObjectShape = {
  width:  opt('nullable-number'),
  height: opt('nullable-number'),
}

const IMAGE_SHAPE: ObjectShape = {
  ...FILE_SHAPE,
  mediaInfo: optShape(MEDIA_INFO_SHAPE),
}

const ADDRESS_SHAPE: ObjectShape = {
  street:       opt('string'),
  streetNumber: opt('string'),
  postalCode:   opt('string'),
  city:         opt('string'),
  state:        opt('string'),
  country:      opt('string'),
  supplement:   opt('string'),
}

const GEOLOCATION_SHAPE: ObjectShape = {
  lat: req('nullable-number'),
  lon: req('nullable-number'),
  accuracy: opt('nullable-number'),
  source: opt('nullable-string'),
  capturedAt: opt('nullable-number'),
}

const CURRENCY_SHAPE: ObjectShape = {
  amount:    req('number'),
  currency:  req('string'),
  precision: req('number'),
}

const DURATION_SHAPE: ObjectShape = {
  days:         opt('nullable-number'),
  hours:        opt('nullable-number'),
  milliseconds: opt('nullable-number'),
  minutes:      opt('nullable-number'),
  months:       opt('nullable-number'),
  quarters:     opt('nullable-number'),
  seconds:      opt('nullable-number'),
  weeks:        opt('nullable-number'),
  years:        opt('nullable-number'),
}

function validateObjectShape(value: any, shape: ObjectShape): boolean {
  if (typeof value !== 'object' || value === null) {
    return false
  }

  // Reject extra keys
  for (const key of Object.keys(value)) {
    if (!(key in shape)) {
      return false
    }
  }

  // Check each defined field
  for (const [key, entry] of Object.entries(shape)) {
    const present = key in value

    if (!present) {
      if (entry.required) {
        return false
      }
      continue
    }

    const v = value[key]

    // Optional fields accept undefined
    if (!entry.required && v === undefined) {
      continue
    }

    if ('shape' in entry) {
      if (v !== null && !validateObjectShape(v, entry.shape)) {
        return false
      }
      continue
    }

    if (!matchesFieldType(v, entry.type)) {
      return false
    }
  }

  return true
}

function matchesFieldType(value: any, type: FieldType): boolean {
  switch (type) {
    case 'string':         return typeof value === 'string'
    case 'number':         return typeof value === 'number'
    case 'boolean':        return typeof value === 'boolean'
    case 'nullable-string': return value === null || typeof value === 'string'
    case 'nullable-number': return value === null || typeof value === 'number'
    default:               return false
  }
}

// Shared suffix for ISO 8601 timezone offsets with optional IANA zone annotation (RFC 9557)
// Matches: Z, +02:00, -05:30, +02:00[Europe/Berlin], Z[UTC]
const ISO_OFFSET_SUFFIX = '(?:Z|[+-]\\d{2}:?\\d{2})?(?:\\[[A-Za-z_/]+\\])?'

function isValidTimeString(value: any) {
  if (typeof value !== 'string') {
    return false
  }

  if (value === '') {
    return true
  }

  // HH:MM, HH:MM:SS, or full ISO time with fractional seconds, offset and optional IANA zone
  // e.g. "08:30", "08:30:00", "12:34:56.789+02:00", "23:59:59Z", "12:00:00+01:00[Europe/Berlin]"
  const match = new RegExp(`^(\\d{2}):(\\d{2})(?::(\\d{2})(?:\\.\\d+)?)?${ISO_OFFSET_SUFFIX}$`).exec(value)

  if (!match) {
    return false
  }

  const hours = Number(match[1])
  const minutes = Number(match[2])
  const seconds = match[3] != null ? Number(match[3]) : 0

  return hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59 && seconds >= 0 && seconds <= 59
}

function isValidDateString(value: any) {
  if (typeof value !== 'string') {
    return false
  }

  if (value === '') {
    return true
  }

  // YYYY-MM-DD (ISO 8601 extended date)
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)

  if (!match) {
    return false
  }

  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])

  if (month < 1 || month > 12 || day < 1) {
    return false
  }

  const daysInMonth = [31, isLeapYear(year) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]
  return day <= daysInMonth[month - 1]
}

function isValidDateTimeString(value: any) {
  if (typeof value !== 'string') {
    return false
  }

  if (value === '') {
    return true
  }

  // ISO 8601 combined date-time with optional fractional seconds, offset and IANA zone
  // e.g. "2024-01-15T14:30:00", "2024-01-15T14:30:00.123+02:00", "2024-01-15T14:30:00Z[UTC]"
  const match = new RegExp(
    `^(\\d{4})-(\\d{2})-(\\d{2})T(\\d{2}):(\\d{2})(?::(\\d{2})(?:\\.\\d+)?)?${ISO_OFFSET_SUFFIX}$`
  ).exec(value)

  if (!match) {
    return false
  }

  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  const hours = Number(match[4])
  const minutes = Number(match[5])
  const seconds = match[6] != null ? Number(match[6]) : 0

  if (month < 1 || month > 12 || day < 1) {
    return false
  }

  const daysInMonth = [31, isLeapYear(year) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]

  return day <= daysInMonth[month - 1] &&
         hours >= 0 && hours <= 23 &&
         minutes >= 0 && minutes <= 59 &&
         seconds >= 0 && seconds <= 59
}

export function extractDatePartFromDateTimeString(value: any): string | null {
  if (!isValidDateTimeString(value)) {
    return null
  }

  return value.substring(0, 10)
}

function isLeapYear(year: number) {
  return (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0)
}

function fieldHasChanged(fieldInfo, oldFieldValue, newFieldValue, options?) {
  if (fieldInfo.cardinality) {
    return arrayOfValuesHasChanged(fieldInfo, oldFieldValue, newFieldValue, options)
  } else {
    return singleValueHasChanged(fieldInfo, oldFieldValue, newFieldValue, options)
  }
}

function arrayOfValuesHasChanged(fieldInfo, oldField, newField, options?) {
  if (oldField == null && newField == null) {
    return false
  }

  if (oldField == null && newField != null) {
    return true
  }

  if (oldField != null && newField == null) {
    return true
  }

  if (oldField.length !== newField.length) {
    return true
  }

  for (let i = 0; i < oldField.length; i += 1) {
    if (singleValueHasChanged(fieldInfo, oldField[i], newField[i], options)) {
      return true
    }
  }
  return false
}

// TODO: Convert types, subtypes, cardinality
function copyFieldsFromDifferentForm(fieldValue, fieldInfo, oldFieldValue?, oldFieldInfo?) {
  if (!oldFieldInfo) {
    return fieldValue
  }

  if (fieldInfo.type === oldFieldInfo.type &&
      fieldInfo.subType === oldFieldInfo.subType) {
    if (fieldInfo.cardinality && oldFieldInfo.cardinality) {
      if (fieldInfo.cardinality.min === oldFieldInfo.cardinality.min &&
          fieldInfo.cardinality.max === oldFieldInfo.cardinality.max) {
          return oldFieldValue
        }
    } else if (!fieldInfo.cardinality && !oldFieldInfo.cardinality) {
      return oldFieldValue
    } else if (fieldInfo.cardinality && !oldFieldInfo.cardinality) {
      return [ oldFieldValue ]
    } else if (!fieldInfo.cardinality && oldFieldInfo.cardinality && oldFieldValue[0]) {
      return oldFieldValue[0]
    }
  }

  return fieldValue
}

function copyFieldFromDifferentForm(fieldValue, fieldInfo, otherFieldValue?, otherFieldInfo?) {
  if (!otherFieldInfo) {
    return fieldValue
  }

  if (fieldInfo.cardinality && otherFieldInfo.cardinality) {
    const max = fieldInfo.cardinality.max === 'n' ? 1000000 :  parseInt(fieldInfo.cardinality.max)
    fieldValue.length = 0

    if (otherFieldValue === undefined) {
      fieldValue = undefined
      return fieldValue
    }

    for (const v of otherFieldValue) {
      if (fieldValue.length >= max) {
        break
      }
      fieldValue.push(tryConvertSingleField(fieldInfo, v, otherFieldInfo))
    }
    return fieldValue
  }
  else if (fieldInfo.cardinality && !otherFieldInfo.cardinality) {
    
    fieldValue.length = 0
    const convertedValue = tryConvertSingleField(fieldInfo, otherFieldValue, otherFieldInfo)

    if (singleValueHasChanged(fieldInfo, fieldInfo.defaultValue, convertedValue)) {
      fieldValue.push(convertedValue)
    }
    
    return fieldValue  
  }
  else if (otherFieldInfo.cardinality) {
    return tryConvertSingleField(fieldInfo, otherFieldValue[0], otherFieldInfo)
  }
  else {
    return tryConvertSingleField(fieldInfo, otherFieldValue, otherFieldInfo)
  }
}


function tryConvertSingleField(targetFieldInfo, sourceFieldValue, sourceFieldInfo) {
  if (targetFieldInfo.type === sourceFieldInfo.type && targetFieldInfo.subType === sourceFieldInfo.subType) {
    return cloneSingleField(targetFieldInfo, sourceFieldValue)
  }

  if ((sourceFieldValue === null) || (sourceFieldValue === undefined)) {
    return cloneSingleField(targetFieldInfo, null)
  }
  
  if (targetFieldInfo.type === 'string') {
    if (sourceFieldInfo.type === 'string' || sourceFieldInfo.type === 'number' || sourceFieldInfo.type === 'boolean') {
      return sourceFieldValue.toString()
    }
  } 

  if (targetFieldInfo.type === 'number') {
    if (sourceFieldInfo.type === 'number') {
      return sourceFieldValue
    }
    if (sourceFieldInfo.type === 'string') {
      return  parseInt(sourceFieldValue) || 0
    }
    if (sourceFieldInfo.type === 'boolean') {
      return sourceFieldValue ? 1 : 0
    }
  }

  return cloneSingleField(targetFieldInfo, null) 
}


function singleValueHasChanged(fieldInfo, oldFieldValue, newFieldValue, options?) {
   switch ( fieldInfo.type ) {
    case 'string':
    case 'number':
    case 'boolean':
      return oldFieldValue !== newFieldValue
    case 'object':
      return objectFieldHasChanged(fieldInfo, oldFieldValue, newFieldValue, options)
    default:
      log.error(`Do not know how to compare field type ${fieldInfo.type}`)
      return JSON.stringify(oldFieldValue) !== JSON.stringify(newFieldValue)
  }
}

function objectFieldHasChanged(fieldInfo, oldFieldValue, newFieldValue, options?) {
  const strict = options ? options.compareStrict === false ? false : true : true

  if (oldFieldValue == null && newFieldValue != null) {
    return true
  }

  if (oldFieldValue != null && newFieldValue == null) {
    return true
  }

  if (oldFieldValue == null && newFieldValue == null) {
    return false
  }

  switch ( fieldInfo.subType ) {
    case 'ref':
      return (oldFieldValue.id     !== newFieldValue.id ||
              oldFieldValue.v      !== newFieldValue.v  ||
              oldFieldValue.text   !== newFieldValue.text ||
              (oldFieldValue.alias ?? undefined)   !== (newFieldValue.alias ?? undefined) ||
              (oldFieldValue.comment ?? undefined) !== (newFieldValue.comment ?? undefined) )
    case 'image':
    case 'file':
      let isDifferentLoose = (oldFieldValue.name !== newFieldValue.name ||
              oldFieldValue.size !== newFieldValue.size ||
              oldFieldValue.type !== newFieldValue.type ||
              oldFieldValue.sha1 !== newFieldValue.sha1 || 
              (oldFieldValue.dataUrl      && !oldFieldValue.dataUrl.startsWith('aeppic-local://')      &&
               newFieldValue.dataUrl      && !newFieldValue.dataUrl.startsWith('aeppic-local://')      && (oldFieldValue.dataUrl !== newFieldValue.dataUrl)) ||
              (oldFieldValue.thumbnailUrl && !oldFieldValue.thumbnailUrl.startsWith('aeppic-local://') && 
               newFieldValue.thumbnailUrl && !newFieldValue.thumbnailUrl.startsWith('aeppic-local://') && (oldFieldValue.thumbnailUrl !== newFieldValue.thumbnailUrl)) ||
              (oldFieldValue.iconUrl      && !oldFieldValue.iconUrl.startsWith('aeppic-local://')      && 
               newFieldValue.iconUrl      && !newFieldValue.iconUrl.startsWith('aeppic-local://')      && (oldFieldValue.iconUrl !== newFieldValue.iconUrl)) ||
              (oldFieldValue.previewUrl   && !oldFieldValue.previewUrl.startsWith('aeppic-local://')   &&
               newFieldValue.previewUrl   && !newFieldValue.previewUrl.startsWith('aeppic-local://')   && (oldFieldValue.previewUrl !== newFieldValue.previewUrl)))

      const isDifferentStrict = (oldFieldValue.dataUrl      !== newFieldValue.dataUrl ||
              oldFieldValue.thumbnailUrl !== newFieldValue.thumbnailUrl ||
              oldFieldValue.iconUrl      !== newFieldValue.iconUrl ||
              oldFieldValue.previewUrl   !== newFieldValue.previewUrl ||
              oldFieldValue.fileInfo?.created   !== newFieldValue.fileInfo?.created ||
              oldFieldValue.fileInfo?.read      !== newFieldValue.fileInfo?.read ||
              oldFieldValue.fileInfo?.modified  !== newFieldValue.fileInfo?.modified ||
              oldFieldValue.fileInfo?.imported  !== newFieldValue.fileInfo?.imported)

      if (fieldInfo.subType === 'image') {
        isDifferentLoose = isDifferentLoose || ( 
          oldFieldValue.mediaInfo?.width  !== newFieldValue.mediaInfo?.width ||
          oldFieldValue.mediaInfo?.height !== newFieldValue.mediaInfo?.height )
      }
      return !!(strict ? isDifferentStrict || isDifferentLoose : isDifferentLoose)
    case 'address':
      return (oldFieldValue.street        !== newFieldValue.street ||
              oldFieldValue.streetNumber  !== newFieldValue.streetNumber ||
              oldFieldValue.postalCode    !== newFieldValue.postalCode ||
              oldFieldValue.city          !== newFieldValue.city ||
              oldFieldValue.state         !== newFieldValue.state ||
              oldFieldValue.country       !== newFieldValue.country ||
              oldFieldValue.supplement    !== newFieldValue.supplement )
    case 'geolocation':
      return (oldFieldValue.lat  !== newFieldValue.lat ||
              oldFieldValue.lon  !== newFieldValue.lon ||
              (oldFieldValue.accuracy ?? null) !== (newFieldValue.accuracy ?? null) ||
              (oldFieldValue.source ?? null) !== (newFieldValue.source ?? null) ||
              (oldFieldValue.capturedAt ?? null) !== (newFieldValue.capturedAt ?? null) )
    case 'duration':
      return (
        oldFieldValue.days !== newFieldValue.days ||
        oldFieldValue.hours !== newFieldValue.hours ||
        oldFieldValue.milliseconds !== newFieldValue.milliseconds ||
        oldFieldValue.minutes !== newFieldValue.minutes ||
        oldFieldValue.months !== newFieldValue.months ||
        oldFieldValue.quarters !== newFieldValue.quarters ||
        oldFieldValue.seconds !== newFieldValue.seconds ||
        oldFieldValue.weeks !== newFieldValue.weeks ||
        oldFieldValue.years !== newFieldValue.years)
    case 'currency': 
      return (
        oldFieldValue.amount !== newFieldValue.amount ||
        oldFieldValue.currency !== newFieldValue.currency ||
        oldFieldValue.precision !== newFieldValue.precision)
    default:
      log.error(`Do not know how to compare object field with subType ${fieldInfo.subType}`)
      return JSON.stringify(oldFieldValue) !== JSON.stringify(newFieldValue)
  }
}

function cloneSingleField(fieldInfo, value) {
  switch ( fieldInfo.type ) {
    case 'string':
    case 'number':
    case 'boolean':
      return value
    case 'object':
      return cloneObject(fieldInfo, value)
    default:
      log.error(`Do not know how to clone field type ${fieldInfo.type}`)
      return JSON.parse(JSON.stringify(value))
  }
}

/**
 * Clone a reference field value. `alias` and `comment` are optional usage
 * metadata (see `Types.Reference`) and are only copied when present, so plain
 * `{ id, v, text }` references keep their exact shape.
 */
function cloneReferenceValue(fieldValue) {
  const reference: Types.Reference = { id: fieldValue.id, v: fieldValue.v, text: fieldValue.text }

  if (fieldValue.alias !== undefined) {
    reference.alias = fieldValue.alias
  }

  if (fieldValue.comment !== undefined) {
    reference.comment = fieldValue.comment
  }

  return reference
}

function cloneObject(fieldInfo, fieldValue) {
  if (typeof fieldValue === 'undefined') {
    return undefined
  }

  if (fieldValue == null) {
    fieldValue = fieldInfo.defaultValue
  }

  switch ( fieldInfo.subType ) {
    case 'ref':
      return cloneReferenceValue(fieldValue)
    
    case 'image':
    case 'file':
      const object: any = {
        name: fieldValue.name,
        size: fieldValue.size,
        type: fieldValue.type,
        sha1: fieldValue.sha1,
        dataUrl: fieldValue.dataUrl,
        thumbnailUrl: fieldValue.thumbnailUrl,
        iconUrl: fieldValue.iconUrl,
        previewUrl: fieldValue.previewUrl ?? null,
      }

      if (fieldValue.fileInfo) {
        object.fileInfo = {
          created: fieldValue.fileInfo.created,
          read: fieldValue.fileInfo.read,
          modified: fieldValue.fileInfo.modified,
          imported: fieldValue.fileInfo.imported
        }

        // Only with dimensions: an empty mediaInfo would make an unchanged
        // value look edited.
        if (fieldInfo.subType === 'image' && (fieldValue.fileInfo.width != null || fieldValue.fileInfo.height != null)) {
          object.mediaInfo = {
            width: fieldValue.fileInfo.width,
            height: fieldValue.fileInfo.height,
          }
        }
      }

      return object
    case 'address':
      return {
        street: fieldValue.street,
        streetNumber: fieldValue.streetNumber,
        postalCode: fieldValue.postalCode,
        city: fieldValue.city,
        state: fieldValue.state,
        country: fieldValue.country,
        supplement: fieldValue.supplement,
      }
    case 'geolocation':
      return {
        lat: fieldValue.lat,
        lon: fieldValue.lon,
        accuracy: fieldValue.accuracy ?? null,
        source: fieldValue.source ?? null,
        capturedAt: fieldValue.capturedAt ?? null,
      }
    case 'duration':
      return {
        days: fieldValue.days,
        hours: fieldValue.hours,
        milliseconds: fieldValue.milliseconds,
        minutes: fieldValue.minutes,
        months: fieldValue.months,
        quarters: fieldValue.quarters,
        seconds: fieldValue.seconds,
        weeks: fieldValue.weeks,
        years: fieldValue.years,
      }
    case 'currency': 
      return {
        amount: fieldValue.amount,
        currency: fieldValue.currency,
        precision: fieldValue.precision,
      }
    default:
      log.error(`Do not know how to clone object field with subType ${fieldInfo.subType}`)
      return JSON.parse(JSON.stringify(fieldValue))
  }
}

function cloneArrayOfValues(fieldInfo, values) {
  if (values == null) {
    return []
  }

  const numberOfEntries = values.length
  const clone = new Array(numberOfEntries)

  for (let i = 0; i < numberOfEntries; i += 1) {
    clone[i] = cloneSingleField(fieldInfo, values[i])
  }

  return clone
}

function compareForms(a: Form, b: Form): number {
  if (!a.document.t && !b.document.t) {
    return a.document.modified.at.localeCompare(b.document.modified.at)
  }

  if (!a.document.t) {
    return 1
  } else if (!b.document.t) {
    return -1
  } else {
    return a.document.t - b.document.t
  }
}

class FormViewRenderForm extends Form {
  private _viewRootSection: IFormSection = null

  constructor(private _canonicalForm: Form, private _view: ParsedFormViewInfo) {
    super(_canonicalForm.document, {
      temporary: _canonicalForm.isTemporary,
      typeValidation: _canonicalForm.typeValidation,
    })
  }

  get info(): ParsedFormInfo {
    return this._view.info
  }

  get fields(): FieldInfo[] {
    return Object.values(this.info.fields)
  }

  get rootSection(): IFormSection {
    if (this._viewRootSection === null) {
      this._viewRootSection = new FormSection(this.info.sections[0], this.info)
    }

    return this._viewRootSection
  }

  getViewInfo(name: string): ParsedFormViewInfo | null {
    return this._canonicalForm.getViewInfo(name)
  }

  hasView(name: string): boolean {
    return this._canonicalForm.hasView(name)
  }

  getFormForView(name: string): Form {
    return this._canonicalForm.getFormForView(name)
  }
}

function _shouldApplyStyleToPlaceholder(placeholder: ParsedPlaceholder, styleName: string, evaluatedConditions: any): boolean {
  const hasTheStyleDefined = placeholder && placeholder.style && placeholder.style[styleName]
  const isConditionalStyle = hasTheStyleDefined && placeholder.style[styleName].condition
  const isConditionMet = !isConditionalStyle || !evaluatedConditions || evaluatedConditions[placeholder.style[styleName].condition.id]

  return !!(hasTheStyleDefined && isConditionMet)
}

const FormViewWarnings = new Set<string>()

function withParsedFormViews(canonicalInfo: ParsedFormInfo, formDocument: Types.Document): ParsedFormInfoWithViews {
  const views = parseFormViews(canonicalInfo, formDocument)

  if (!views || Object.keys(views).length === 0) {
    return { ...canonicalInfo }
  }

  return {
    ...canonicalInfo,
    views
  }
}

function parseFormViews(canonicalInfo: ParsedFormInfo, formDocument: Types.Document): ParsedFormViews {
  const viewNames = formDocument?.data?.viewNames
  const viewDefinitions = formDocument?.data?.viewDefinitions

  if (!Array.isArray(viewNames) || !Array.isArray(viewDefinitions)) {
    return null
  }

  if (viewNames.length !== viewDefinitions.length) {
    logInvalidFormViewsOnce(
      formDocument,
      `Ignoring Form Views for form "${formDocument?.id}" because viewNames and viewDefinitions have different lengths.`,
    )
    return null
  }

  const views: ParsedFormViews = {}

  for (let i = 0; i < viewNames.length; i += 1) {
    const name = normalizeFormViewName(viewNames[i])
    const definition = typeof viewDefinitions[i] === 'string' ? viewDefinitions[i] : ''

    if (!name || definition.trim().length === 0) {
      continue
    }

    if (views[name]) {
      warnInvalidFormViewOnce(formDocument, name, `Duplicate Form View name "${name}". Keeping the first view.`)
      continue
    }

    const viewInfo = parseSingleFormView(canonicalInfo, formDocument, name, definition)

    if (viewInfo) {
      views[name] = {
        name,
        definition,
        info: viewInfo,
      }
    }
  }

  return views
}

function parseSingleFormView(canonicalInfo: ParsedFormInfo, formDocument: Types.Document, name: string, definition: string): ParsedFormInfo | null {
  let viewInfo: ParsedFormInfo

  try {
    viewInfo = parseFormDown(definition)
  } catch (error) {
    warnInvalidFormViewOnce(formDocument, name, `Could not parse Form View "${name}".`, error)
    return null
  }

  const unknownFields = Object.keys(viewInfo.fields).filter(fieldName => !canonicalInfo.fields[fieldName])

  if (unknownFields.length > 0) {
    warnInvalidFormViewOnce(formDocument, name, `Form View "${name}" references unknown field(s): ${unknownFields.join(', ')}`)
    return null
  }

  for (const placeholder of viewInfo.placeholders) {
    const fieldName = placeholder.field?.name

    if (!fieldName) {
      continue
    }

    placeholder.field = canonicalInfo.fields[fieldName]
  }

  viewInfo.fields = canonicalInfo.fields
  viewInfo.conditions = canonicalInfo.conditions

  return viewInfo
}

function normalizeFormViewName(value: unknown): string {
  return typeof value === 'string' ? value.trim() : ''
}

function warnInvalidFormViewOnce(formDocument: Types.Document, viewName: string, message: string, error?: unknown) {
  const warningKey = `${formDocument?.id ?? ''}@${formDocument?.v ?? ''}:${viewName}:${message}`

  if (FormViewWarnings.has(warningKey)) {
    return
  }

  FormViewWarnings.add(warningKey)
  console.warn(message, {
    formId: formDocument?.id,
    formVersion: formDocument?.v,
    viewName,
    error,
  })
}

function logInvalidFormViewsOnce(formDocument: Types.Document, message: string) {
  const warningKey = `${formDocument?.id ?? ''}@${formDocument?.v ?? ''}:${message}`

  if (FormViewWarnings.has(warningKey)) {
    return
  }

  FormViewWarnings.add(warningKey)
  console.error(message, {
    formId: formDocument?.id,
    formVersion: formDocument?.v,
  })
}

const FormsCache = new Map<string, ParsedFormInfo>()

function parseForm(definition: string): ParsedFormInfo {
  let parsed = FormsCache.get(definition)

  if (!parsed) {
    parsed = parseFormDown(definition)
    FormsCache.set(definition, parsed)
  }

  return parsed
}

// ── Format validation ──────────────────────────────────────────────

const PHONE_REGEX = /^\+?[\d\s\-().]{7,}$/
const URL_REGEX = /^https?:\/\/.+/
const IBAN_REGEX = /^[A-Z]{2}\d{2}[A-Z0-9]{4,}$/

type FormatRule = { name: string, args: string[] }

function parseFormatRules(formatString: string): FormatRule[] {
  const rules: FormatRule[] = []
  let depth = 0
  let current = ''

  for (let i = 0; i < formatString.length; i++) {
    const ch = formatString[i]
    if (ch === '(' || ch === '[') depth++
    else if (ch === ')' || ch === ']') depth--
    else if (ch === ',' && depth === 0) {
      rules.push(parseOneRule(current.trim()))
      current = ''
      continue
    }
    current += ch
  }

  if (current.trim()) {
    rules.push(parseOneRule(current.trim()))
  }

  return rules
}

function parseOneRule(rule: string): FormatRule {
  const parenIndex = rule.indexOf('(')

  if (parenIndex === -1) {
    return { name: rule, args: [] }
  }

  const name = rule.substring(0, parenIndex)
  const argsString = rule.substring(parenIndex + 1, rule.lastIndexOf(')'))

  // Strip surrounding quotes from each arg
  const args = argsString.split(',').map(a => {
    a = a.trim()
    if ((a.startsWith("'") && a.endsWith("'")) || (a.startsWith('"') && a.endsWith('"'))) {
      return a.slice(1, -1)
    }
    return a
  })

  return { name, args }
}

function applyFormatRule(rule: FormatRule, value: string, label: string): FieldValidationError | null {
  switch (rule.name) {
    case 'email': {
      if (!value.match(EMAIL_REGEX)) {
        const text = `${label} is not a valid email address`
        return { text, html: text }
      }
      return null
    }
    case 'phone': {
      if (!PHONE_REGEX.test(value)) {
        const text = `${label} is not a valid phone number`
        return { text, html: text }
      }
      return null
    }
    case 'url': {
      if (!URL_REGEX.test(value)) {
        const text = `${label} is not a valid URL`
        return { text, html: text }
      }
      return null
    }
    case 'iban': {
      const stripped = value.replace(/\s/g, '')
      if (!IBAN_REGEX.test(stripped)) {
        const text = `${label} is not a valid IBAN`
        return { text, html: text }
      }
      return null
    }
    case 'min-length': {
      const min = parseInt(rule.args[0]) || 0
      if (value.length < min) {
        const text = `${label} must be at least ${min} characters`
        return { text, html: text }
      }
      return null
    }
    case 'max-length': {
      const max = parseInt(rule.args[0]) || Infinity
      if (value.length > max) {
        const text = `${label} must be at most ${max} characters`
        return { text, html: text }
      }
      return null
    }
    case 'match': {
      const pattern = rule.args[0]
      if (!pattern) {
        return null
      }

      try {
        const regex = new RegExp(pattern)
        if (!regex.test(value)) {
          const text = `${label} does not match the required format`
          return { text, html: text }
        }
      } catch (e) {
        console.warn(`Invalid format regex pattern "${pattern}" for field ${label}`)
      }
      return null
    }
    default: {
      console.warn(`Unknown format validator "${rule.name}" for field ${label}`)
      return null
    }
  }
}

function normalizeValidationTag(tag: ParsedTag): string[] {
  if (!tag || tag === true) {
    return []
  }

  const value = typeof tag === 'string'
    ? tag
    : typeof tag === 'object' && 'value' in tag && typeof tag.value === 'string'
      ? tag.value
      : ''

  return value
    .split(',')
    .map(name => name.trim())
    .filter(name => name.length > 0)
}
